<template>
  <Txt class="mt-4 flex items-center gap-2 text-base font-medium">{{ $t('Company.GeneralInfo') }}</Txt>
  <p class="text-sm text-gray-600">{{ $t('Company.GeneralInfoDescription')}}</p>

  <CGBanner
    :title="$t('Company.BasicInfo')"
    :subtitle="$t('Company.BasicInfoDescription')"
    variant="blue"
  >
    <template #icon>
      <Icon name="info" size="22" class="text-blue-600" />
    </template>
  </CGBanner>

  <div class="space-y-8">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4 mt-4">
      <div class="space-y-2">
        <FormGroup :label="$t('Company.EnterCompanyName')" required html-for="companyName" :noMargin="true">
          <CGInput
            id="companyName"
            v-model="draftCompany.displayname"
            name="companyName"
            autocomplete="companyName"
            required
            @change="draftCompany.name = cleanCompanyName(draftCompany.displayname)"
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('General.LegalCompanyName')" required html-for="legal_company_name" :noMargin="true">
          <CGInput
            id="legal_company_name"
            v-model="draftCompany.legal_company_name"
            name="legal_company_name"
            autocomplete="legal_company_name"
            required
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('General.Vat')" required html-for="vat" :noMargin="true">
          <CGInput
            id="vat"
            v-model="draftCompany.vat"
            name="vat"
            autocomplete="vat"
            required
            :errored="!!invalidVatError" 
            :error-message="invalidVatError"
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('General.Country')" required html-for="country" :noMargin="true" :autoWidth="true">
          <CGSelect
            id="country"
            v-model="draftCompany.country"
            :placeholder="$t('Company.SelectCountry')"
            :options="countriesOptions"
            required
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('Company.Subdomain')" required html-for="subDomain" :noMargin="true">
          <CGInput
            id="subDomain"
            v-model="draftCompany.name"
            name="subDomain"
            autocomplete="subDomain"
            required
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('Profile.Timezone')" required html-for="timezone" :noMargin="true" :autoWidth="true">
          <CGSelect
            id="timezone"
            v-model="draftCompany.timezone"
            :placeholder="$t('General.SelectTimezone')"
            :options="$timezones.labels()"
            required
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('Company.SupportedLanguages')" required html-for="supportedLanguages" :noMargin="true">
          <InputTags
            id="supported_languages"
            v-model="draftCompany.supported_languages"
            :options="supportedLanguages"
            required
            @remove="removeLangOpt"
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup  :label="$t('Company.DefaultLanguage')" required html-for="companyDefLang" :noMargin="true" :autoWidth="true">
          <CGSelect
            id="companyDefLang"
            v-model="draftCompany.default_language"
            :options="draftCompany.supported_languages"
            required
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('Company.SupportAddress')" html-for='emailCustomDomain' no-margin required>
          <CGInput
            id='emailCustomDomain'
            v-model='draftCompany.supportAddress'
            :placeholder="supportAddressPlaceholder"
            :error-message='supportEmailError'
            :errored='!!supportEmailError'
            required
            @update:modelValue='validateSupportEmail'
          />
        </FormGroup>
      </div>
      <div class="space-y-2">
        <FormGroup :label="$t('CgaTypeConfiguration.BoardTrainingType')" required html-for="boardType" :noMargin="true" :autoWidth="true">
          <CGSelect
            id="boardType"
            v-model="draftCompany.boardTrainingType"
            :placeholder="$t('General.SelectBoardTrainingType')"
            :options="boardTrainingTypeOptions"
            required
          />
        </FormGroup>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Roles, CyberGuru } from '@/common/constants';
import localizationService from '@/services/localization.service.js'

export default defineComponent({
    props: {
    company: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      draftCompany: { ...this.company },
      supportEmailError: '',
      supportedLanguages: [],
    };
  },
  watch: {
    company: {
      handler(newVal) {
        this.draftCompany = { ...newVal };
      },
      deep: true
    }
  },
  async mounted() {
    if (this.$company.customization_enabled && this.$profile.hasRoles(Roles.MSP)) {
      this.supportedLanguages = await this.getSupportedLanguages();
      } else {
        this.supportedLanguages = this.$languages;
      }
  },
  computed: {
    supportAddressPlaceholder() {
      if (this.$profile.hasRoles(Roles.MSP)) {
        const parentCompanyEmailDomain = this.$profile.companies.find(c=>c.company_id == this.$profile.company_id)?.email_domain;
        return parentCompanyEmailDomain? `noreply${this.$methods.splitString(parentCompanyEmailDomain)[0]}` : CyberGuru.NoreplyEmail;
      }
      
      return CyberGuru.SupportEmail;
    },
    invalidVatError() {
      if(!this.draftCompany.vat) return '';
      return this.draftCompany.vat.length > 20 ? this.$t('Company.VatTooLong') : 
        !(/^[a-zA-Z0-9]+$/).test(this.draftCompany.vat) ? this.$t('Company.InvalidVatFormat'): '';
    },
    countriesOptions () {
      const countriesDict = this.$isoCountriesLanguages.getCountries(this.$i18n.locale) || {};
      return Object.keys(countriesDict).map(el => ({
        value: el,
        label: countriesDict[el] 
      }));
    },
    boardTrainingTypeOptions() {
      return this.$cgaType.boardTrainingTypes();
    }
  },
  methods: {
    validateSupportEmail(value) {
      this.supportEmailError = !this.isValidEmail(value)? 
        this.$t('Company.InvalidSupportEmailValue', { email: this.draftCompany.supportAddress }) : null;
    },
    isValidEmail(value) {
      return /^\S+@\S+\.\S+$/.exec(value);
    },
    cleanCompanyName(name) {
      return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    },
    removeLangOpt(langOpts) {
      this.draftCompany.supported_languages = langOpts;
      if (!langOpts.find(lang=>lang.value == this.draftCompany.default_language)) {
        this.draftCompany.default_language = null; 
      }
    },
    async getSupportedLanguages() {
      try {
        const res = await localizationService.getCompanyLangList(this.$company.company_id);
        return res.data.map(el => ({ value: el.lang_code, label: el.name }));
      } catch (err) {
        console.error(err);
        this.$eventBus.$emit('show-alert', { 
          title: this.$t('General.DataFetchError'), 
          variant: 'danger' 
        });
      }
    }
  }
});
</script>